import jwtDecode from "jwt-decode";
import router from "@/router";
import { initState } from "./index";
import refreshToken from "@/services/refreshToken";
import getSPUser from "@/services/getSPUser";

const IS_DEV_MODE = process.env.NODE_ENV === "development";

const AUTH_TOKEN = process.env.VUE_APP_AUTH_TOKEN_NAME;
const SP_HOST = process.env.VUE_APP_SP_HOST;
const { VUE_APP_SP_HOST, VUE_APP_SP_SITE_URL } = process.env;

const URL_LOGIN_APP = `https://${VUE_APP_SP_HOST}/${VUE_APP_SP_SITE_URL}/login`;

// Thời gian đệm 5 phút trước khi hết hạn để tự động refresh ngầm
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

let msTokenTimeOutId = null;
let ictTokenTimeOutId = null;
let safetyIntervalId = null;
let listenerRegistered = false;
let isRefreshingMsToken = false;

export default {
  login: ({ commit, dispatch }, payload) => {
    localStorage.setItem(AUTH_TOKEN, payload);
    const jwtExpDecode = jwtDecode(payload);
    dispatch("initMsToken", payload);
    commit("setAuth", jwtExpDecode);
    dispatch("checkTimeout", jwtExpDecode.exp);
    dispatch("setupTokenAutoRefreshListeners");
    // so sánh user với SP
    if (SP_HOST) {
      dispatch("checkCurrentUserEmailEqualSPUser", jwtExpDecode);
    }
  },
  logout: ({ commit }) => {
    if (msTokenTimeOutId) clearTimeout(msTokenTimeOutId);
    if (ictTokenTimeOutId) clearTimeout(ictTokenTimeOutId);
    if (safetyIntervalId) clearInterval(safetyIntervalId);

    localStorage.removeItem(AUTH_TOKEN);
    commit("setAuth", initState);
    window.location.replace(
      `https://${VUE_APP_SP_HOST}/${VUE_APP_SP_SITE_URL}/logout/?full=true`
    );
  },
  // Payload: exp timestamp in seconds
  checkTimeout: ({ dispatch }, payload) => {
    if (ictTokenTimeOutId) {
      clearTimeout(ictTokenTimeOutId);
    }
    if (!payload) return;

    const now = Date.now();
    const expMs = payload * 1000;
    const timeLeft = expMs - now;

    if (timeLeft <= 0) {
      console.warn("[Auth] Access token đã hết hạn.");
      dispatch("logout");
      return;
    }

    // Đặt lịch kiểm tra/refresh ngầm trước 5 phút khi token hết hạn
    const refreshDelay = Math.max(0, timeLeft - REFRESH_BUFFER_MS);
    ictTokenTimeOutId = setTimeout(() => {
      dispatch("checkTokenStatus");
    }, refreshDelay);
  },
  init: ({ dispatch, state }) => {
    const token = localStorage.getItem(AUTH_TOKEN) || state?.token;
    if (token) {
      dispatch("login", token);
    } else {
      if (IS_DEV_MODE) {
        router.push("/auth/login");
      } else {
        window.location.replace(`${URL_LOGIN_APP}/?to=${window.location.href}`);
      }
    }
  },
  /**
   * MS TOKEN
   */
  // Payload: exp timestamp in seconds
  checkTimeoutMsToken: ({ dispatch }, payload) => {
    if (msTokenTimeOutId) {
      clearTimeout(msTokenTimeOutId);
    }
    if (!payload) return;

    const now = Date.now();
    const expMs = payload * 1000;
    const timeLeft = expMs - now;

    // Refresh trước khi msToken hết hạn 5 phút
    const refreshDelay = timeLeft - REFRESH_BUFFER_MS;

    if (refreshDelay <= 0) {
      // console.log(
      //   "[Auth] msToken sắp hoặc đã hết hạn, tự động refresh ngầm ngay lập tức..."
      // );
      const currentAuthToken = localStorage.getItem(AUTH_TOKEN);
      if (currentAuthToken) {
        dispatch("initMsToken", currentAuthToken);
      }
    } else {
      // console.log(
      //   `[Auth] Đã hẹn giờ refresh ngầm msToken sau ${Math.round(
      //     refreshDelay / 1000
      //   )}s`
      // );
      msTokenTimeOutId = setTimeout(() => {
        const currentAuthToken = localStorage.getItem(AUTH_TOKEN);
        if (currentAuthToken) {
          dispatch("initMsToken", currentAuthToken);
        }
      }, refreshDelay);
    }
  },
  initMsToken: async ({ dispatch, commit }, payload) => {
    const tokenToUse = payload || localStorage.getItem(AUTH_TOKEN);
    if (!tokenToUse) {
      router.push("/auth/login");
      return;
    }

    if (isRefreshingMsToken) return;
    isRefreshingMsToken = true;

    try {
      // Send a POST request
      const { data } = await refreshToken(tokenToUse);
      // console.log("initMsToken > refreshMsToken response", data);
      if (data?.data?.refreshMsToken) {
        // set msToken
        const newMsToken = data.data.refreshMsToken;
        commit("setMsToken", newMsToken);
        // call check timeout
        const jwtExpDecode = jwtDecode(newMsToken);
        dispatch("checkTimeoutMsToken", jwtExpDecode.exp);
      } else {
        console.warn("> Lỗi get new refreshMsToken");
      }
    } catch (error) {
      console.error("> Lỗi refreshToken", error);
    } finally {
      isRefreshingMsToken = false;
    }
  },
  // Kiểm tra định kỳ và khi người dùng active lại tab
  checkTokenStatus: ({ dispatch, state }) => {
    const token = localStorage.getItem(AUTH_TOKEN) || state?.token;
    if (!token) return;

    try {
      // 1. Kiểm tra Access token
      const jwtExpDecode = jwtDecode(token);
      const now = Date.now();
      const tokenTimeLeft = jwtExpDecode.exp * 1000 - now;

      if (tokenTimeLeft <= 0) {
        console.warn("[Auth] Access token đã hết hạn khi kiểm tra.");
        dispatch("logout");
        return;
      }

      // 2. Kiểm tra msToken
      if (state.msToken) {
        try {
          const msJwtExpDecode = jwtDecode(state.msToken);
          const msTimeLeft = msJwtExpDecode.exp * 1000 - now;
          if (msTimeLeft <= REFRESH_BUFFER_MS) {
            // console.log("[Auth] msToken cần làm mới khi kiểm tra trạng thái.");
            dispatch("initMsToken", token);
          }
        } catch (e) {
          console.warn(
            "[Auth] Lỗi decode msToken, tiến hành lấy lại msToken",
            e
          );
          dispatch("initMsToken", token);
        }
      } else {
        dispatch("initMsToken", token);
      }
    } catch (error) {
      console.error("[Auth] Lỗi khi kiểm tra trạng thái token", error);
    }
  },
  // Đăng ký các event listener khi chuyển tab/focus và timer an toàn
  setupTokenAutoRefreshListeners: ({ dispatch }) => {
    if (!listenerRegistered) {
      listenerRegistered = true;

      window.addEventListener("focus", () => {
        // console.log("[Auth] Phát hiện focus tab, kiểm tra trạng thái token...");
        dispatch("checkTokenStatus");
      });

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          // console.log(
          //   "[Auth] Tab chuyển sang hiển thị, kiểm tra trạng thái token..."
          // );
          dispatch("checkTokenStatus");
        }
      });

      // Kiểm tra an toàn định kỳ mỗi 2 phút
      if (!safetyIntervalId) {
        safetyIntervalId = setInterval(() => {
          dispatch("checkTokenStatus");
        }, 2 * 60 * 1000);
      }
    }
  },
  // Kiểm tra nếu user SP !== user Graph thì logout
  checkCurrentUserEmailEqualSPUser: async ({ dispatch }, payload) => {
    try {
      const { data } = await getSPUser();
      if (data?.d?.Email) {
        if (data.d.Email !== payload.email?.toString()) {
          localStorage.removeItem(AUTH_TOKEN);
          window.location.replace(
            `${URL_LOGIN_APP}/?to=${window.location.href}`
          );
        }
      }
    } catch (error) {
      console.log("Error checkCurrentUserEmailEqualSPUser: ", error);
    }
  },
};
